import React, { useState, useEffect } from 'react';
import { X, Download, Share, Bell } from 'lucide-react';
import { getDeviceContext, checkIfAppInstalled } from '../utils/deviceContext';
import { useAuth } from '../contexts/AuthContext';
import { usePushNotifications } from '../hooks/usePushNotifications';

/**
 * InstallBanner — Phase 3 TWA / PWA install prompt
 *
 * Android Chrome (not installed): captures `beforeinstallprompt` → shows "Add to Home Screen" banner
 * iOS non-Safari (Chrome/Firefox): shows "Open in Safari to install" nudge
 * TWA / Standalone / Desktop: renders nothing
 */
export default function InstallBanner() {
  const { token } = useAuth();
  const { isSupported, subscribe } = usePushNotifications(token);
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const ctx = getDeviceContext();

    // Never show in standalone / TWA / desktop
    if (ctx.shell === 'desktop') return;
    if (ctx.isStandalone || ctx.shell === 'twa') {
      // User is inside the installed app. Show Notification prompt if needed.
      const hasNotificationApi = typeof window !== 'undefined' && 'Notification' in window;
      if (token && isSupported && hasNotificationApi && Notification.permission === 'default' && !sessionStorage.getItem('notifBannerDismissed')) {
        setMode('enable-notifications');
        setShow(true);
      }
      return; // Do not proceed to the install logic
    }

    // Check if user dismissed in this session
    if (sessionStorage.getItem('installBannerDismissed')) return;

    if (ctx.shell === 'ios-other-browser') {
      // iOS Chrome/Firefox: can't install — nudge to Safari
      setMode('ios-use-safari');
      setShow(true);
      return;
    }

    if (ctx.shell === 'android-browser') {
      // Android Chrome: capture beforeinstallprompt
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        // Before showing, check if already installed
        checkIfAppInstalled().then((installed) => {
          if (!installed) {
            setMode('android-a2hs');
            setShow(true);
          }
        });
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }

    if (ctx.shell === 'ios-safari') {
      // iOS Safari: show manual Add to Home Screen guide after 4s
      const t = setTimeout(() => {
        if (!sessionStorage.getItem('installBannerDismissed')) {
          setMode('ios-a2hs');
          setShow(true);
        }
      }, 4000);
      return () => clearTimeout(t);
    }
  }, [token, isSupported]);

  const handleDismiss = () => {
    setShow(false);
    sessionStorage.setItem('installBannerDismissed', '1');
  };

  const handleDismissNotif = () => {
    setShow(false);
    sessionStorage.setItem('notifBannerDismissed', '1');
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setShow(false);
    setDeferredPrompt(null);
  };

  if (!show) return null;

  // ── Android: "Add to Home Screen" prompt ──────────────────────────────────
  if (mode === 'android-a2hs') {
    return (
      <div
        className="fixed bottom-0 left-0 right-0 z-50 p-4"
        data-testid="install-banner-android"
      >
        <div className="bg-[#18181B] rounded-2xl px-4 py-4 flex items-center gap-3 shadow-2xl max-w-md mx-auto">
          <div className="w-10 h-10 rounded-xl bg-[#E8430A]/20 flex items-center justify-center flex-shrink-0">
            <Download className="w-5 h-5 text-[#E8430A]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-semibold leading-tight">Add Match It Up to Home Screen</p>
            <p className="text-white/50 text-xs">Faster access. Works offline. No app store needed.</p>
          </div>
          <button
            onClick={handleInstall}
            className="flex-shrink-0 text-xs font-bold text-white bg-[#E8430A] px-3 py-2 rounded-full active:scale-95 transition-transform"
            data-testid="install-banner-android-btn"
          >
            Add
          </button>
          <button onClick={handleDismiss} className="flex-shrink-0 p-1 text-white/40 hover:text-white/80" data-testid="install-banner-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ── iOS Safari: manual Add to Home Screen guide ───────────────────────────
  if (mode === 'ios-a2hs') {
    return (
      <div
        className="fixed bottom-0 left-0 right-0 z-50 p-4"
        data-testid="install-banner-ios-safari"
      >
        <div className="bg-[#18181B] rounded-2xl px-4 py-4 shadow-2xl max-w-md mx-auto">
          <div className="flex items-start justify-between gap-2 mb-2">
            <p className="text-white text-sm font-semibold">Install Match It Up</p>
            <button onClick={handleDismiss} className="p-1 text-white/40 hover:text-white/80 flex-shrink-0" data-testid="install-banner-dismiss">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-3 text-white/70 text-xs">
            <Share className="w-4 h-4 text-[#E8430A] flex-shrink-0" />
            <span>Tap the <strong className="text-white">Share</strong> icon → <strong className="text-white">Add to Home Screen</strong></span>
          </div>
        </div>
      </div>
    );
  }

  // ── iOS Chrome/Firefox: nudge to open in Safari ───────────────────────────
  if (mode === 'ios-use-safari') {
    return (
      <div
        className="fixed bottom-0 left-0 right-0 z-50 p-4"
        data-testid="install-banner-ios-safari-nudge"
      >
        <div className="bg-[#18181B] rounded-2xl px-4 py-3 flex items-center gap-3 shadow-2xl max-w-md mx-auto">
          <div className="flex-1 min-w-0">
            <p className="text-white text-xs font-semibold">For the best experience, open in Safari</p>
            <p className="text-white/50 text-xs">Tap Share → Open in Safari → Add to Home Screen</p>
          </div>
          <button onClick={handleDismiss} className="flex-shrink-0 p-1 text-white/40 hover:text-white/80" data-testid="install-banner-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ── PWA/TWA: Enable Notifications prompt ──────────────────────────────────
  if (mode === 'enable-notifications') {
    return (
      <div
        className="fixed bottom-0 left-0 right-0 z-50 p-4"
        data-testid="install-banner-notif"
      >
        <div className="bg-[#18181B] rounded-2xl px-4 py-4 flex items-center gap-3 shadow-2xl max-w-md mx-auto">
          <div className="w-10 h-10 rounded-xl bg-[#E8430A]/20 flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-[#E8430A]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-semibold leading-tight">Enable Notifications</p>
            <p className="text-white/50 text-xs">Never miss a match or message.</p>
          </div>
          <button
            onClick={async () => {
              await subscribe();
              setShow(false);
            }}
            className="flex-shrink-0 text-xs font-bold text-white bg-[#E8430A] px-3 py-2 rounded-full active:scale-95 transition-transform"
          >
            Enable
          </button>
          <button onClick={handleDismissNotif} className="flex-shrink-0 p-1 text-white/40 hover:text-white/80">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
